import AnimalTypes from "@wesstron/utils/Api/constants/animalTypes";
import {maxBy, minBy} from "lodash";
import memoizeOne from "memoize-one";
import {FilterTypes} from "../constans/filter";
import {ParamTypes} from "../constans/paramTypes";
import groupsDB from "../database/groupsDB";
import {createKeyToValueDictionary} from "./Utils";
import i18n from "../i18n";

export function createGroupTypesArray(t) {
    return [
        {name: t("group"), value: "group_nr"},
        {name: t("printSelectedAnimalsModal.cycle"), value: "cycle"},
        {name: t("cycleWeek"), value: "cycleWeek"},
        {name: t("animalCycleDay"), value: "cycleDay"},
        {name: t("value"), value: "[value]"},
        {name: t("animals"), value: "AnmID"},
        {name: t("time"), value: "dataEv"},
        {name: t("grid.weekDay"), value: "day"},
        {name: t("month"), value: "month"},
        {name: t("week"), value: "week"},
        {name: t("year"), value: "year"},
        {name: t("dictionary"), value: "DictionaryID"},
        {name: t("placement"), value: "PlcmntID"},
        {name: t("race"), value: "race"},
        {name: t("age"), value: "age"},
        {name: t("dayFromInsertionSimple"), value: "insertionDay"},
        {
            name: t("chamber.separationCageInfo.fatteningDay"),
            value: "fatteningDay",
        },
        {name: t("user"), value: "user"},
        {name: t("groupDay"), value: "groupDay"},
        {name: t("animalType"), value: "AnimalKind"},
    ];
}

export function createFilterTypesArray(t) {
    return [
        {name: t("time"), value: "time"},
        {name: t("technologyGroup"), value: "technologyGroup"},
        {name: t("group"), value: "group"},
        {name: t("cycle"), value: "cycle"},
        {name: t("inventory"), value: "inventory"},
    ];
}

const createGroupTypesDictionary = memoizeOne((t) => {
    const result = createGroupTypesArray(t);
    return createKeyToValueDictionary(result, "value");
});

export function translateGroupName(group, t) {
    const dictionary = createGroupTypesDictionary(t);
    return dictionary[group]?.name || group;
}

function getOptionFormatter(row) {
    switch (row.parameter) {
        case "location":
            return ParamTypes.LOCATION;
        case "status":
            return ParamTypes.SOW_STATE;
        case "dataEv":
            return ParamTypes.TIME;
        case "tattoo":
        case "motherTattoo":
        case "fatherTattoo":
            return ParamTypes.TATTOO;
        case "motherNumber":
        case "fatherNumber":
            return ParamTypes.ANIMAL;
        case "race":
        case "motherRace":
        case "fatherRace":
            return ParamTypes.RACE;
        case "rfid":
            return ParamTypes.RFID;
        case "herdNumber":
            return ParamTypes.HERD_NUMBER;
        case "supplier":
            return ParamTypes.SUPPLIER;
        case "insertionTime":
            return ParamTypes.TIME;
        case "age":
            return ParamTypes.AGE;
        default:
            return null;
    }
}

export function getParameterFormatter(row, keysDictionary) {
    if (
        [
            "COUNT_VALUES",
            "COUNT_VALUES_DISTINCT",
            "COUNT_ANIMAL",
            "COUNT_ANIMAL_DISTINCT",
        ].includes(row.type)
    ) {
        return ParamTypes.AMOUNT;
    } else {
        if (row.parameter === "MATH") return ParamTypes.AMOUNT;
        if (row.isOptionsParameter) return getOptionFormatter(row);
        const data = keysDictionary[row.parameter];
        if (!data) return null;
        return data.type;
    }
}

export function getFilterType(parameterData, parameter) {
    switch (parameterData.type) {
        case ParamTypes.AGE:
        case ParamTypes.AMOUNT:
        case ParamTypes.DAYS:
        case ParamTypes.INDEX:
        case ParamTypes.PERCENT:
        case ParamTypes.PRICE:
        case ParamTypes.WEIGHT:
            return FilterTypes.NUMBER;
        case ParamTypes.ANIMAL:
            // TODO dodac kiedys inne typy dla parametrow
            switch (parameter) {
                case "boar":
                    return FilterTypes.BOARS;
                default:
                    return FilterTypes.STRING;
            }
        case ParamTypes.BOOLEAN:
            return FilterTypes.BOOLEAN;
        case ParamTypes.PARTURITION_LEVEL:
            return FilterTypes.PARTURITION_LEVEL;
        case ParamTypes.REASON:
            return FilterTypes.FALL_REASON;
        case ParamTypes.RESULT:
            return FilterTypes.USG_RESULT;
        case ParamTypes.SECTOR_TYPE:
            return FilterTypes.SECTOR_TYPES;
        case ParamTypes.TECHNOLOGY_GROUP_REMOVAL:
            return FilterTypes.TECHNOLOGY_GROUP_REMOVAL;
        case ParamTypes.TIME:
            return FilterTypes.DATE;
        case ParamTypes.SOW_STATE:
            return FilterTypes.SOW_STATE;
        default:
            return FilterTypes.STRING;
    }
}

export function getDetailsFilterType(parameter, keysDictionary) {
    if (
        [
            "COUNT_ANIMAL",
            "COUNT_ANIMAL_DISTINCT",
            "COUNT_VALUES",
            "COUNT_VALUES_DISTINCT",
        ].includes(parameter.type)
    ) {
        return FilterTypes.NUMBER;
    }
    return getFilterType(
        keysDictionary[parameter.parameter],
        parameter.parameter
    );
}

export function getDefaultFilterType(filterType, firstCycleIndex) {
    switch (filterType) {
        case "group":
            return {pickerType: "group"};
        case "technologyGroup":
            return {unit: "week", group: 0, pickerType: "technologyGroup"};
        case "cycle":
            return {unit: "cycle", value: firstCycleIndex, pickerType: "cycle"};
        case "inventory":
            return {
                unit: "animalKind",
                value: [AnimalTypes.SOW],
                pickerType: "inventory",
            };
        default:
            return {
                time: 7,
                unit: "days",
                pickerType: "time",
            };
    }
}

export function translateGroupNames(filterType, groups, farm) {
    const obj = {};
    if (filterType === "technologyGroup") {
        for (let group of groups) {
            obj[group] = group;
        }
    } else if (filterType === "group") {
        for (let group of groups) {
            let groupObject = groupsDB.getGroupByAnmGrp(group, farm);
            obj[group] = groupObject?.GrNo1;
        }
    } else if (filterType === "cycle") {
        for (let group of groups) {
            if (typeof group === "number") {
                obj[group] = `${i18n.t("cycle")} ${group}`;
            } else if (group.includes("-")) {
                const [start, end] = group.split("-");
                obj[group] = `${i18n.t("cycle")} ${start}-${end}`;
            } else {
                obj[group] = `${i18n.t("cycle")} ${group}`;
            }
        }
    }
    return obj;
}

export function calculateLegendResult(data, field, type) {
    switch (type) {
        case "AVG":
            const sum = data.reduce((acc, item) => acc + (item[field] || 0), 0);
            return sum / data.length;
        case "MIN":
            return minBy(data, field)?.[field] || 0;
        case "MAX":
            return maxBy(data, field)?.[field] || 0;
        default:
            return data.reduce((acc, item) => acc + (item[field] || 0), 0);
    }
}

export function getXAxisFormatterType(group, keysDictionary, parameter) {
    switch (group) {
        case "AnmID":
            return ParamTypes.ANIMAL;
        case "dataEv":
            return ParamTypes.TIME;
        case "day":
            return ParamTypes.WEEK_DAY;
        case "DictionaryID":
            return ParamTypes.DICTIONARY;
        case "PlcmntID":
            return ParamTypes.LOCATION;
        case "race":
            return ParamTypes.RACE;
        case "user":
            return ParamTypes.USER;
        case "[value]": {
            const data = keysDictionary[parameter];
            return data?.type;
        }
        case "group_nr": {
            return ParamTypes.GROUP_NR;
        }
        case "month": {
            return ParamTypes.MONTH;
        }
        case "AnimalKind":
            return ParamTypes.ANIMAL_KIND;
        default:
            return null;
    }
}
